import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import { UserOutlined, MailOutlined, LockOutlined, PhoneOutlined } from "@ant-design/icons";
import axiosInstance from "../../Config/axiosConfig";
import "./SignUp.css";
import Logo from "../../Components/Logo/Logo";

function Signup({ setLoggedIn, deviceId, userIp }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [phoneNumber, setPhoneNumber] = useState(""); // Initialize empty

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPasswordErrors({
      hasLower: /[a-z]/.test(value),
      hasUpper: /[A-Z]/.test(value),
      hasNumber: /\d/.test(value),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)
    });
  };

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
    
    // If user starts with 0, replace with 255
    if (value.startsWith('0')) {
      value = '255' + value.substring(1);
    }
    // If user doesn't start with country code, add it (unless empty)
    else if (value && !value.startsWith('255')) {
      value = '255' + value;
    }
    
    // Limit to 12 digits total (255 + 9 digits)
    if (value.length > 12) {
      value = value.substring(0, 12);
    }
    
    setPhoneNumber(value);
    form.setFieldsValue({ phoneNumber: value });
  };

  const formatPhoneNumberForDisplay = (value) => {
    if (!value) return '';
    
    // If starts with 255, show as 255 XXX XXX XXX
    if (value.startsWith('255') && value.length > 3) {
      const rest = value.substring(3);
      return `255 ${rest.substring(0, 3)} ${rest.substring(3, 6)} ${rest.substring(6)}`;
    }
    
    return value;
  };

  const handleSignup = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      if (values.email) {
        formData.append('email', values.email);
      }
      
      // Ensure phone number is properly formatted
      let phone = values.phoneNumber.replace(/\D/g, '');
      if (phone.startsWith('0')) {
        phone = '255' + phone.substring(1);
      } else if (!phone.startsWith('255')) {
        phone = '255' + phone;
      }
      
      formData.append('name', values.username);
      formData.append('password', values.password);
      formData.append('phoneNumber', phone);
      formData.append('role', 2); // Student role

      const response = await axiosInstance.post('/user/signup',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            UserDevice: deviceId,
            UserIPAddress: userIp,
          },
        }
      );
      
      if (response.data.code === 200) {
        message.success(response.data.message || 'Registration successful!');
        
        if (values.email) {
          navigate('/verify-signup-otp', { 
            state: { 
              email: values.email,
              deviceId,
              userIp
            } 
          });
        } else {
          navigate('/signin', { 
            state: { 
              phoneNumber: phone,
              deviceId,
              userIp
            } 
          });
        }
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error ||
                         'Registration failed. Please try again.';
      message.error(errorMessage);
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page-container">
      <div className="signup-container">
        <Logo />
        <h4>Sign up for your account</h4>
        <span className="signup-text">Welcome! Register your credentials</span>

        <Form form={form} className="signup-form" layout="vertical" onFinish={handleSignup} requiredMark={false}>
          <Form.Item
            name="username"
            rules={[
              { required: true, message: "Please enter your full name" },
              { min: 3, message: "Name must be at least 3 characters" }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter your full name" 
              className="signup-input" 
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: false, message: "Please enter your email (Optional)" },
              { 
                type: "email", 
                message: "Please enter a valid email",
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value && value.trim() !== value) {
                    return Promise.reject("Remove spaces from email");
                  }
                  return Promise.resolve();
                }
              },
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Enter your email (Optional)" 
              className="signup-input" 
              onBlur={(e) => e.target.value = e.target.value.trim()}
            />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            rules={[
              { required: true, message: "Please enter your phone number" },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject('Please enter your phone number');
                  }
                  const digits = value.replace(/\D/g, '');
                  // Check if starts with 255 and has 12 digits total or starts with 0 and would be 12 digits when converted
                  if (
                    (digits.startsWith('255') && digits.length !== 12) ||
                    (digits.startsWith('0') && digits.length !== 10) ||
                    (!digits.startsWith('255') && !digits.startsWith('0') && digits.length !== 9)
                  ) {
                    return Promise.reject('Please enter a valid phone number (255XXXXXXXXX or 0XXXXXXXXX)');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              prefix={<PhoneOutlined />} 
              placeholder="255XXXXXXXXX or 0XXXXXXXXX" 
              className="signup-input"
              value={formatPhoneNumberForDisplay(phoneNumber)}
              onChange={handlePhoneNumberChange}
              maxLength={15} // Allow for spaces in display
            />
          </Form.Item>

          {/* Rest of your form remains the same */}
          <Form.Item
            name="password"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 8, message: "Password must be at least 8 characters" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  
                  const hasLower = /[a-z]/.test(value);
                  const hasUpper = /[A-Z]/.test(value);
                  const hasNumber = /\d/.test(value);
                  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);
                  
                  if (!hasLower) {
                    return Promise.reject("Password needs at least 1 lowercase letter");
                  }
                  if (!hasUpper) {
                    return Promise.reject("Password needs at least 1 uppercase letter");
                  }
                  if (!hasNumber) {
                    return Promise.reject("Password needs at least 1 number");
                  }
                  if (!hasSpecial) {
                    return Promise.reject("Password needs at least 1 special character (!@#$%^&*)");
                  }
                  
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Enter your password" 
              className="signup-input"
              onChange={handlePasswordChange}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject('The two passwords do not match');
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Confirm your password" 
              className="signup-input" 
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="signup-button" 
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Sign Up'}
            </Button>
          </Form.Item>
        </Form>

        <p className="signin-link">
          Already have an account? <a href="/signin">Sign In</a>
        </p>
      </div>
    </div>
  );
}

export default Signup;